import { FC, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import MainLayout from 'src/pages/private';
import { useFormik } from 'formik';
import { voucherPaymentInitialValues } from 'src/utils/initialValues.utils';
import { voucherPaymentDynamicValidation } from 'src/utils/validationScheme.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  apiUrls,
  routeURL,
  pageTitles,
  voucherPaymentDialogTitles,
  voucherPaymentDialogSubtitles,
  DATE_FORMATS,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import VoucherPaymentGrid from 'src/components/Grids/VoucherPaymentGrid';
import AddVoucherPaymentDialog from 'src/components/DialogBox/VoucherPaymentDialog/AddVoucherPaymentDialog';
import TextField from 'src/components/InputFields/TextField';
import PaymentModePicker from 'src/components/InputFields/PaymentModePicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { StyledDatePickerErrorText } from 'src/components/StyledComponents/StyledUnitPicker';
import ErrorIcon from 'src/components/InputFields/ErrorIcon';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
import { VoucherPaymentPayload } from 'src/models/VoucherPayment.interface';
import {
  VoucherPaymentPageHeaderBox,
  VoucherPaymentTitleSection,
  VoucherPaymentBackButton,
  VoucherPaymentBackIcon,
  VoucherPaymentPageTitle,
  VoucherPaymentPrimaryActionButton,
  VoucherPaymentButtonIcon,
  VoucherPaymentPageSubtitle,
} from 'src/components/StyledComponents/VoucherPayments.styles';
import {
  VoucherPaymentFieldRow,
  VoucherPaymentFieldItem,
  VoucherPaymentFieldItemEnd,
  VoucherPaymentChequeRow,
  VoucherPaymentOnlineRow,
} from 'src/components/DialogBox/VoucherPaymentDialog/VoucherPaymentDialog.styles';
import FormikSelectField from 'src/components/InputFields/FormikSelectField';
import { useGetBasicBanks } from 'src/services/bank.service';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';

interface LocationState {
  billNumber?: string;
}

const VoucherPayments: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { voucherId } = useParams<{ voucherId: string }>();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentAdded, setPaymentAdded] = useState(false);
  const [billAmount, setBillAmount] = useState<number>(0);
  const [totalPaidAmount, setTotalPaidAmount] = useState<number>(0);

  const locationState = location.state as LocationState | null;
  const [billNumber, setBillNumber] = useState<string>(
    locationState?.billNumber || ''
  );

  const { showSnackbar } = StyledSnackbar();
  const { basicBanks, fetchBasicBanks } = useGetBasicBanks();

  const accountOptions = basicBanks.map((bank) => ({
    label: `${bank.bankName} - ${bank.accountNumber}`,
    value: bank.id.toString(),
  }));

  const fetchVoucherDetails = async () => {
    if (voucherId) {
      try {
        const response = await axiosTokenInstance.get(
          apiUrls.GET_VOUCHER_BY_ID,
          { params: { id: voucherId } }
        );
        if (!locationState?.billNumber) {
          setBillNumber(response.data?.billNumber || '');
        }
        setBillAmount(Number(response.data?.billAmount) || 0);
        setTotalPaidAmount(Number(response.data?.totalPaidAmount) || 0);
      } catch (error) {
        console.error('Failed to fetch voucher details:', error);
      }
    }
  };

  const handleFetchBanksIfNeeded = (paymentMode: PaymentMode | '') => {
    if (
      (paymentMode === PaymentMode.CHEQUE ||
        paymentMode === PaymentMode.ONLINE) &&
      basicBanks.length === 0
    ) {
      fetchBasicBanks();
    }
  };

  const remainingAmount = Math.max(billAmount - totalPaidAmount, 0);

  const handleClickOpen = async () => {
    await fetchVoucherDetails();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const handleBack = () => {
    if (voucherId) {
      navigate(
        `${routeURL.USER}${routeURL.VOUCHER_MANAGEMENT}?view=${voucherId}`
      );
    } else {
      navigate(routeURL.USER + routeURL.VOUCHER_MANAGEMENT);
    }
  };

  const formik = useFormik({
    initialValues: voucherPaymentInitialValues,
    validationSchema: voucherPaymentDynamicValidation,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => {
      // Submission handled in handleSavePayment
    },
  });

  const handleSavePayment = async () => {
    if (loading) return;

    const touchedFields: Record<string, boolean> = {
      receiverName: true,
      paymentDate: true,
      amount: true,
      paymentMode: true,
    };

    if (formik.values.paymentMode === PaymentMode.CHEQUE) {
      touchedFields.chequeNumber = true;
      touchedFields.chequeDate = true;
      touchedFields.bankAccountId = true;
    } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
      touchedFields.bankAccountId = true;
      touchedFields.transactionReference = true;
    }

    try {
      await voucherPaymentDynamicValidation.validate(formik.values, {
        abortEarly: false,
      });
    } catch (validationError) {
      const yupError = validationError as {
        inner: Array<{ path: string; message: string }>;
      };
      const errors = yupError.inner.reduce(
        (
          acc: Record<string, string>,
          error: { path: string; message: string }
        ) => {
          acc[error.path] = error.message;
          return acc;
        },
        {}
      );
      await formik.setTouched(touchedFields, false);
      formik.setErrors(errors);
      return;
    }

    const enteredAmount = parseFloat(formik.values.amount.replace(/,/g, ''));
    if (remainingAmount > 0 && enteredAmount > remainingAmount) {
      await formik.setTouched({ ...touchedFields }, false);
      formik.setErrors({
        amount: `Amount exceeds remaining payable amount (${remainingAmount.toLocaleString()})`,
      });
      return;
    }

    setLoading(true);

    try {
      const payload: VoucherPaymentPayload = {
        voucherId: voucherId || '',
        receiverName: formik.values.receiverName,
        paymentDate: dayjs(formik.values.paymentDate).format(DATE_FORMATS.API),
        paidAmount: parseFloat(formik.values.amount.replace(/,/g, '')),
        paymentMode: formik.values.paymentMode,
      };

      if (formik.values.paymentMode === PaymentMode.CHEQUE) {
        payload.chequeNumber = formik.values.chequeNumber;
        payload.chequeDate = dayjs(formik.values.chequeDate).format(
          DATE_FORMATS.API
        );
        payload.bankAccountId = formik.values.bankAccountId;
      } else if (formik.values.paymentMode === PaymentMode.ONLINE) {
        payload.bankAccountId = formik.values.bankAccountId;
        payload.paymentRefNo = formik.values.transactionReference;
      }

      const response = await axiosTokenInstance.post(
        apiUrls.ADD_VOUCHER_PAYMENT,
        payload
      );
      showSnackbar(response.data.message, 'success');
      setPaymentAdded(!paymentAdded);
      handleClose();
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchBanksIfNeeded(formik.values.paymentMode);
  }, [formik.values.paymentMode]);

  return (
    <MainLayout title="Voucher Management">
      {!voucherId ? (
        <Typography>Invalid Voucher ID</Typography>
      ) : (
        <>
          <VoucherPaymentPageHeaderBox>
            <VoucherPaymentTitleSection>
              <VoucherPaymentBackButton onClick={handleBack}>
                <VoucherPaymentBackIcon
                  component="i"
                  className="bx bx-chevron-left"
                />
              </VoucherPaymentBackButton>
              <VoucherPaymentPageTitle>
                {pageTitles.VOUCHER_PAYMENTS}
              </VoucherPaymentPageTitle>
              {billNumber && (
                <VoucherPaymentPageSubtitle>
                  #{billNumber}
                </VoucherPaymentPageSubtitle>
              )}
            </VoucherPaymentTitleSection>
            <VoucherPaymentPrimaryActionButton onClick={handleClickOpen}>
              <VoucherPaymentButtonIcon component="i" className="bx bx-plus" />
              New Payment
            </VoucherPaymentPrimaryActionButton>
          </VoucherPaymentPageHeaderBox>

          <VoucherPaymentGrid
            voucherId={voucherId}
            state={paymentAdded}
            banks={basicBanks}
            onBanksFetch={() => fetchBasicBanks()}
            onPaymentModeChange={handleFetchBanksIfNeeded}
          />

          <AddVoucherPaymentDialog
            open={open}
            handleClose={handleClose}
            title={voucherPaymentDialogTitles.ADD}
            subtitle={voucherPaymentDialogSubtitles.ADD}
            onSubmit={formik.handleSubmit}
            buttonDisabled={loading}
            buttonLabel="Save"
            onSaveClick={handleSavePayment}
            paymentModeSection={
              <>
                <Box mb={0}>
                  <PaymentModePicker
                    color="var(--subtext)"
                    width="48.5%"
                    id="paymentMode"
                    label="Payment Method"
                    formikProps={formik}
                    customMessage="Select payment method"
                    showLabel={false}
                  />
                </Box>
                {formik.values.paymentMode === PaymentMode.CHEQUE && (
                  <>
                    <VoucherPaymentChequeRow>
                      <VoucherPaymentFieldItem>
                        <TextField
                          color="var(--subtext)"
                          width="100%"
                          type="number"
                          id="chequeNumber"
                          label="Cheque Number"
                          formikProps={formik}
                        />
                      </VoucherPaymentFieldItem>
                      <VoucherPaymentFieldItem>
                        <CreatedDatePicker
                          fontSize="14px"
                          height="40px"
                          color="var(--subtext)"
                          width="100%"
                          name="chequeDate"
                          placeholder="Select date"
                          formikProps={formik}
                          showLabel={true}
                          label="Cheque Date"
                        />
                        {formik.touched.chequeDate &&
                          formik.errors.chequeDate && (
                            <StyledDatePickerErrorText>
                              <ErrorIcon />
                              {formik.errors.chequeDate}
                            </StyledDatePickerErrorText>
                          )}
                      </VoucherPaymentFieldItem>
                    </VoucherPaymentChequeRow>
                    <VoucherPaymentFieldRow>
                      <VoucherPaymentFieldItem>
                        <FormikSelectField
                          id="bankAccountId"
                          label="Bank Account"
                          formikProps={formik}
                          width="48.5%"
                          options={accountOptions}
                        />
                      </VoucherPaymentFieldItem>
                    </VoucherPaymentFieldRow>
                  </>
                )}
                {formik.values.paymentMode === PaymentMode.ONLINE && (
                  <VoucherPaymentOnlineRow>
                    <VoucherPaymentFieldItem>
                      <FormikSelectField
                        id="bankAccountId"
                        label="Bank Account"
                        formikProps={formik}
                        width="100%"
                        options={accountOptions}
                      />
                    </VoucherPaymentFieldItem>
                    <VoucherPaymentFieldItemEnd>
                      <TextField
                        color="var(--subtext)"
                        width="100%"
                        id="transactionReference"
                        label="Payment Reference"
                        formikProps={formik}
                      />
                    </VoucherPaymentFieldItemEnd>
                  </VoucherPaymentOnlineRow>
                )}
              </>
            }
          >
            <VoucherPaymentFieldRow>
              <VoucherPaymentFieldItem>
                <TextField
                  color="var(--subtext)"
                  width="100%"
                  id="receiverName"
                  label="Receiver Name"
                  formikProps={formik}
                />
              </VoucherPaymentFieldItem>

              <VoucherPaymentFieldItemEnd>
                <CreatedDatePicker
                  mb={3}
                  fontSize="14px"
                  label="Payment Date"
                  showLabel={true}
                  color="var(--subtext)"
                  width="100%"
                  height="40px"
                  name="paymentDate"
                  placeholder="Payment Date"
                  formikProps={formik}
                />
                {formik.touched.paymentDate && formik.errors.paymentDate && (
                  <StyledDatePickerErrorText top="2px">
                    <ErrorIcon />
                    {formik.errors.paymentDate}
                  </StyledDatePickerErrorText>
                )}
              </VoucherPaymentFieldItemEnd>
            </VoucherPaymentFieldRow>
            <VoucherPaymentFieldRow>
              <VoucherPaymentFieldItem>
                {' '}
                <TextField
                  color="var(--subtext)"
                  width="100%"
                  id="amount"
                  label="Amount"
                  formikProps={formik}
                  type="text"
                  value={formatNumberWithCommas(formik.values.amount)}
                  onChange={(e) => handleNumberFieldChange(e, formik, 'amount')}
                  placeholder={
                    remainingAmount > 0
                      ? `Remaining: ${remainingAmount.toLocaleString()}`
                      : ''
                  }
                />
              </VoucherPaymentFieldItem>
            </VoucherPaymentFieldRow>
          </AddVoucherPaymentDialog>
        </>
      )}
    </MainLayout>
  );
};

export default VoucherPayments;
